import { getUtmShareUrl } from '@/app/utils/urlHelper';
import { ShareArticleTracking, ShareJobTracking, useAnalytics } from '@/utils/useAnalytics';
import { useCloseOnOutsideClick } from '@/utils/useCloseOnOutsideClick';
import dynamic from 'next/dynamic';
import React, { useRef, useState } from 'react';

import Button from '@material-ui/core/Button';
import Fade from '@material-ui/core/Fade';
import type { PopperPlacementType } from '@material-ui/core/Popper';
import Popper from '@material-ui/core/Popper';
import ShareOutlinedIcon from '@material-ui/icons/ShareOutlined';

import styles from './ShareContainer.module.scss';

const ShareEmail = dynamic(() => import('@/components/ShareEmail'));
const ShareFacebook = dynamic(() => import('@/components/ShareFacebook'));
const ShareLink = dynamic(() => import('@/components/ShareLink'));

export type ShareContainerProps = {
  className?: string;
  emailTitle: string | JSX.Element;
  emailSubject: string;
  emailBody: string;
  facebookTitle: string;
  shareUrl?: string;
  shareTrackingProps:
    | { name: 'Share Job'; details: ShareJobTracking }
    | { name: 'Share Article'; details: ShareArticleTracking };
  id: string | number;
  popperPlacement?: PopperPlacementType;
  jobOrArticleShare: 'jobshare' | 'articleshare';
};

const ShareContainer = ({
  className,
  emailTitle,
  emailSubject,
  emailBody,
  facebookTitle,
  shareUrl,
  shareTrackingProps,
  id,
  popperPlacement = 'bottom',
  jobOrArticleShare,
}: ShareContainerProps) => {
  const url = shareUrl ?? globalThis.location?.href;
  const [isShareOpen, setIsShareOpen] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useCloseOnOutsideClick(isShareOpen && !isDialogOpen, setIsShareOpen);
  const popperId = `share-dropdown_${id}`;
  const track = useAnalytics();

  return (
    <div ref={containerRef} className={className}>
      <Button
        variant="outlined"
        color="secondary"
        data-testid={'share-dropdown-button'}
        ref={shareButtonRef}
        onClick={() => {
          setIsShareOpen(!isShareOpen);
          track('Click Share button', shareTrackingProps.details);
        }}
        aria-controls={popperId}
        aria-haspopup="true"
        aria-expanded={isShareOpen}
        startIcon={<ShareOutlinedIcon />}
        fullWidth
      >
        Share
      </Button>
      {isShareOpen ? (
        <Popper
          id={popperId}
          data-testid={'share-dropdown'}
          open={isShareOpen}
          anchorEl={shareButtonRef.current}
          placement={popperPlacement}
          transition
          disablePortal
          className={styles.popper}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <div className={styles.dropdownContainer}>
                <div className={styles.item}>
                  <ShareLink
                    shareUrl={getUtmShareUrl(jobOrArticleShare, url, 'copy')}
                    onClick={() => {
                      setIsShareOpen(false);
                      track(shareTrackingProps.name, {
                        ...shareTrackingProps.details,
                        'Share Type': 'Link',
                      });
                    }}
                    className={styles.button}
                  />
                </div>
                <div className={styles.item}>
                  <ShareEmail
                    title={emailTitle}
                    subject={emailSubject}
                    body={emailBody}
                    onClick={() => {
                      setIsShareOpen(false);
                      track(shareTrackingProps.name, {
                        ...shareTrackingProps.details,
                        'Share Type': 'Email',
                      });
                    }}
                    className={styles.button}
                    onDialogStatusChange={isDialogOpen => setIsDialogOpen(isDialogOpen)}
                  />
                </div>
                <div className={styles.item}>
                  <ShareFacebook
                    title={facebookTitle}
                    shareUrl={getUtmShareUrl(jobOrArticleShare, url, 'social')}
                    onClick={() => {
                      setIsShareOpen(false);
                      track(shareTrackingProps.name, {
                        ...shareTrackingProps.details,
                        'Share Type': 'Facebook',
                      });
                    }}
                    className={styles.button}
                  />
                </div>
              </div>
            </Fade>
          )}
        </Popper>
      ) : (
        <></>
      )}
    </div>
  );
};

export default ShareContainer;
